// import { memo, useEffect, useMemo, useState } from 'react';
// import { Box, Grid, Skeleton, Text, Flex } from '@chakra-ui/react';
// import { useDispatch, useSelector } from 'react-redux';
// import ProductItemNew from 'components/Dashboard/ProductItemNew';
// import { get_product_by_brand } from 'redux/actions/productActions';

// /* ─────────────────────────────────────────────────────────────
//    BRAND TOKENS (kept inline so this file is independent)
//    ───────────────────────────────────────────────────────────── */
// const INK         = '#1A2740';
// const MUTED       = '#5A6B7E';
// // const FAINT       = '#8896AE';
// const SURFACE     = '#FFFFFF';
// const SURFACE_ALT = '#F7F9FB';
// const BORDER      = '#E8EDF5';
// const FONT = "'Bricolage Grotesque', sans-serif";

// /* Normalize for case-insensitive name comparison */
// const norm = (s) => String(s ?? '').trim().toLowerCase();

// /* ─────────────────────────────────────────────────────────────
//    SkeletonCard — placeholder while loading
//    ───────────────────────────────────────────────────────────── */
// const SkeletonCard = () => (
//     <Box
//         borderRadius="10px"
//         overflow="hidden"
//         bg={SURFACE}
//         border="1px solid"
//         borderColor={BORDER}
//     >
//         <Skeleton h="160px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
//         <Box p="10px">
//             <Skeleton h="3" w="80%" mb="2" startColor={SURFACE_ALT} endColor={BORDER} />
//             <Skeleton h="3" w="50%" startColor={SURFACE_ALT} endColor={BORDER} />
//         </Box>
//     </Box>
// );

// /* ─────────────────────────────────────────────────────────────
//    SIMILAR PRODUCTS
//    ───────────────────────────────────────────────────────────── */
// const SimilarProducts = memo(function SimilarProducts({ productDetail }) {
//     const dispatch = useDispatch();
//     const { brands, brandItems, loading } = useSelector(
//         (state) => state.productReducer
//     );

//     /* Track whether we've at least attempted a fetch for this brand,
//        so we can distinguish "loading" from "tried and got nothing". */
//     const [hasFetched, setHasFetched] = useState(false);

//     /* Resolve the brand to fetch products for — try multiple field
//        names because Zoho data is inconsistent (some products use
//        `brand`, some use `mgs_brand`, some use `custom_field_hash.cf_brand`). */
//     const productBrandName = useMemo(() => {
//         if (!productDetail) return null;
//         return (
//             productDetail.brand ??
//             productDetail.mgs_brand ??
//             productDetail.custom_field_hash?.cf_brand ??
//             null
//         );
//     }, [productDetail]);

//     /* Find the matching brand in Redux, CASE-INSENSITIVELY.
//        This is the key fix — the old code used strict === which failed
//        when "Adidas" in the brands list didn't match "adidas" on the product. */
//     const matchedBrand = useMemo(() => {
//         if (!productBrandName || !Array.isArray(brands)) return null;
//         const target = norm(productBrandName);
//         return brands.find((b) => norm(b?.name) === target) ?? null;
//     }, [brands, productBrandName]);

//     /* Fetch products for the matched brand */
//     useEffect(() => {
//         if (!matchedBrand?.name) return;
//         dispatch(get_product_by_brand(matchedBrand.name));
//         setHasFetched(true);
//         /* Re-fetch when the product changes (different brand) */
//     }, [dispatch, matchedBrand?.name]);

//     /* Filter out the current product from the suggestions —
//        a Taja Shirt page shouldn't suggest Taja Shirt as a similar product. */
//     const similarProducts = useMemo(() => {
//         const items = brandItems?.items ?? [];
//         if (items.length === 0) return [];
//         const currentId = productDetail?.item_id;
//         const filtered = currentId
//             ? items.filter((item) => item.item_id !== currentId)
//             : items;
//         return filtered.slice(0, 5);
//     }, [brandItems, productDetail?.item_id]);

//     /* ── Render states ─────────────────────────────────────── */

//     /* If we don't even know the brand name, nothing to show */
//     if (!productBrandName) return null;

//     /* If brands are still loading OR we haven't fetched yet AND nothing is in brandItems → skeleton */
//     const isWaiting =
//         !hasFetched ||
//         (loading && similarProducts.length === 0) ||
//         !Array.isArray(brands) ||
//         brands.length === 0;

//     if (isWaiting && similarProducts.length === 0) {
//         return (
//             <Box w="full">
//                 <Grid
//                     templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }}
//                     gap={{ base: '8px', md: '12px' }}
//                     w="full"
//                 >
//                     {Array.from({ length: 5 }).map((_, i) => (
//                         <SkeletonCard key={i} />
//                     ))}
//                 </Grid>
//             </Box>
//         );
//     }

//     /* Empty state — we tried but got nothing useful */
//     if (similarProducts.length === 0) {
//         return (
//             <Box w="full">
//                 <Flex
//                     direction="column"
//                     align="center"
//                     justify="center"
//                     py="32px"
//                     px="20px"
//                     textAlign="center"
//                     gap="6px"
//                     bg={SURFACE}
//                     border="1px solid"
//                     borderColor={BORDER}
//                     borderRadius="12px"
//                 >
//                     <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT}>
//                         No similar products yet
//                     </Text>
//                     <Text fontSize="11px" color={MUTED}>
//                         Check back soon — we&apos;re adding new {productBrandName} items regularly.
//                     </Text>
//                 </Flex>
//             </Box>
//         );
//     }

//     /* Render the grid */
//     return (
//         <Box w="full">
//             <Grid
//                 templateColumns={{
//                     base: 'repeat(2, 1fr)',
//                     sm: 'repeat(3, 1fr)',
//                     md: 'repeat(4, 1fr)',
//                     lg: 'repeat(5, 1fr)',
//                 }}
//                 gap={{ base: '8px', md: '12px' }}
//                 w="full"
//             >
//                 {similarProducts.map((item) => (
//                     <Box
//                         key={item.item_id}
//                         minW="0"
//                         w="full"
//                         display="flex"
//                         flexDirection="column"
//                         h="full"
//                         sx={{
//                             '& > *': {
//                                 flex: 1,
//                                 height: '100%',
//                                 marginBottom: '0 !important',
//                             },
//                         }}
//                     >
//                         <ProductItemNew item={item} />
//                     </Box>
//                 ))}
//             </Grid>
//         </Box>
//     );
// });

// export default SimilarProducts;

import { memo, useEffect, useMemo, useState } from 'react';
import { Box, Skeleton, Text, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import ProductItemNew from 'components/Dashboard/ProductItemNew';
import { get_product_by_brand } from 'redux/actions/productActions';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS (kept inline so this file is independent)
   ───────────────────────────────────────────────────────────── */
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const FONT = "'Bricolage Grotesque', sans-serif";

/* Normalize for case-insensitive name comparison */
const norm = (s) => String(s ?? '').trim().toLowerCase();

/* ─────────────────────────────────────────────────────────────
   SkeletonCard — placeholder while loading
   ───────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
    <Box
        borderRadius="10px"
        overflow="hidden"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
    >
        <Skeleton h="160px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
        <Box p="10px">
            <Skeleton h="3" w="80%" mb="2" startColor={SURFACE_ALT} endColor={BORDER} />
            <Skeleton h="3" w="50%" startColor={SURFACE_ALT} endColor={BORDER} />
        </Box>
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   SIMILAR PRODUCTS
   ───────────────────────────────────────────────────────────── */
const SimilarProducts = memo(function SimilarProducts({ productDetail }) {
    const dispatch = useDispatch();
    const { brands, brandItems, loading } = useSelector(
        (state) => state.productReducer
    );

    /* Track whether we've at least attempted a fetch for this brand,
       so we can distinguish "loading" from "tried and got nothing". */
    const [hasFetched, setHasFetched] = useState(false);

    /* Resolve the brand to fetch products for — try multiple field
       names because Zoho data is inconsistent (some products use
       `brand`, some use `mgs_brand`, some use `custom_field_hash.cf_brand`). */
    const productBrandName = useMemo(() => {
        if (!productDetail) return null;
        return (
            productDetail.brand ??
            productDetail.mgs_brand ??
            productDetail.custom_field_hash?.cf_brand ??
            null
        );
    }, [productDetail]);

    /* Find the matching brand in Redux, CASE-INSENSITIVELY.
       This is the key fix — the old code used strict === which failed
       when "Adidas" in the brands list didn't match "adidas" on the product. */
    const matchedBrand = useMemo(() => {
        if (!productBrandName || !Array.isArray(brands)) return null;
        const target = norm(productBrandName);
        return brands.find((b) => norm(b?.name) === target) ?? null;
    }, [brands, productBrandName]);

    /* Fetch products for the matched brand */
    useEffect(() => {
        if (!matchedBrand?.name) return;
        dispatch(get_product_by_brand(matchedBrand.name));
        setHasFetched(true);
        /* Re-fetch when the product changes (different brand) */
    }, [dispatch, matchedBrand?.name]);

    /* Filter out the current product from the suggestions —
       a Taja Shirt page shouldn't suggest Taja Shirt as a similar product. */
    const similarProducts = useMemo(() => {
        const items = brandItems?.items ?? [];
        if (items.length === 0) return [];
        const currentId = productDetail?.item_id;
        const filtered = currentId
            ? items.filter((item) => item.item_id !== currentId)
            : items;
        /* Mobile shows 2x2 grid (4 items).
           Desktop shows horizontal scroll (10 items, scroll through). */
        return filtered.slice(0, 10);
    }, [brandItems, productDetail?.item_id]);

    /* ── Render states ─────────────────────────────────────── */

    /* If we don't even know the brand name, nothing to show */
    if (!productBrandName) return null;

    /* If brands are still loading OR we haven't fetched yet AND nothing is in brandItems → skeleton */
    const isWaiting =
        !hasFetched ||
        (loading && similarProducts.length === 0) ||
        !Array.isArray(brands) ||
        brands.length === 0;

    if (isWaiting && similarProducts.length === 0) {
        return (
            <Box w="full">
                <Flex
                    gap={{ base: '10px', md: '14px' }}
                    w="full"
                    overflowX="auto"
                    pb="8px"
                    sx={{
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-track': { background: SURFACE_ALT, borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: BORDER, borderRadius: '4px' },
                        '@media (max-width: 767px)': {
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                        },
                    }}
                >
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Box
                            key={i}
                            w={{ base: '160px', sm: '180px', md: '220px' }}
                            flexShrink={0}
                        >
                            <SkeletonCard />
                        </Box>
                    ))}
                </Flex>
            </Box>
        );
    }

    /* Empty state — we tried but got nothing useful */
    if (similarProducts.length === 0) {
        return (
            <Box w="full">
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py="32px"
                    px="20px"
                    textAlign="center"
                    gap="6px"
                    bg={SURFACE}
                    border="1px solid"
                    borderColor={BORDER}
                    borderRadius="12px"
                >
                    <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT}>
                        No similar products yet
                    </Text>
                    <Text fontSize="11px" color={MUTED}>
                        Check back soon — we&apos;re adding new {productBrandName} items regularly.
                    </Text>
                </Flex>
            </Box>
        );
    }

    /* Render — horizontal scroll carousel on both mobile and desktop.
       Card width scales by viewport so it feels right on phone vs desktop. */
    return (
        <Box w="full" position="relative">
            <Flex
                gap={{ base: '10px', md: '14px' }}
                w="full"
                overflowX="auto"
                pb="10px"
                sx={{
                    scrollSnapType: 'x mandatory',
                    scrollPadding: '0 8px',
                    /* WebKit scrollbar styling */
                    '&::-webkit-scrollbar': { height: '6px' },
                    '&::-webkit-scrollbar-track': {
                        background: SURFACE_ALT,
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: BORDER,
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#CBD5E0',
                    },
                    /* Smooth scroll behavior */
                    scrollBehavior: 'smooth',
                    /* Hide scrollbar on mobile (looks cleaner — gesture is the affordance) */
                    '@media (max-width: 767px)': {
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        '-ms-overflow-style': 'none',
                    },
                }}
            >
                {similarProducts.map((item) => (
                    <Box
                        key={item.item_id}
                        /* Mobile cards are ~45% of viewport so 2 are visible
                           with a hint of the 3rd peeking from the right edge.
                           Desktop cards have a fixed comfortable width. */
                        w={{ base: '160px', sm: '180px', md: '220px' }}
                        flexShrink={0}
                        scrollSnapAlign="start"
                        display="flex"
                        flexDirection="column"
                        h="full"
                        sx={{
                            '& > *': {
                                flex: 1,
                                height: '100%',
                                marginBottom: '0 !important',
                            },
                        }}
                    >
                        <ProductItemNew item={item} />
                    </Box>
                ))}
            </Flex>
        </Box>
    );
});

export default SimilarProducts;